import { ReactNode } from "react";
import { Row } from "@tanstack/react-table";

import { TableGroupRow } from "ui/data-grid/components/table-view/table-group-row";
import { TableDataRow } from "ui/data-grid/components/table-view/table-data-row";
import { TableSkeletonRows } from "ui/data-grid/components/table-view/table-skeleton-rows";
import { TableEmptyState } from "ui/data-grid/components/table-view/table-empty-state";

import { resolveDraggableRowId } from "ui/data-grid/helpers/resolve-draggable-row-id";

import { EmptyGroupMeta, TableStyles } from "ui/data-grid/data-grid.types";

type TableBodyContentProps<T, TEmptyGroupData> = {
  rows: Row<T>[];
  emptyGroups: EmptyGroupMeta<TEmptyGroupData>[];
  getGroupedNode?: (row: Row<T>) => ReactNode;
  getEmptyGroupNode?: (emptyGroup: EmptyGroupMeta<TEmptyGroupData>) => ReactNode;
  isDraggable: boolean;
  draggableId?: string;
  tableId?: string;
  renderSpacer: boolean;
  leftColumnCount: number;
  rightColumnCount: number;
  styles?: TableStyles;
  isLoading: boolean;
  emptyMessage?: string;
  emptyState?: ReactNode;
  skeletonRowCount: number;
};

export const TableBodyContent = <T, TEmptyGroupData = unknown>({
  rows,
  emptyGroups,
  getGroupedNode,
  getEmptyGroupNode,
  isDraggable,
  draggableId,
  tableId,
  renderSpacer,
  leftColumnCount,
  rightColumnCount,
  styles,
  isLoading,
  emptyMessage,
  emptyState,
  skeletonRowCount,
}: TableBodyContentProps<T, TEmptyGroupData>) => {
  const { rowClassName, cellClassName, groupRowClassName, groupCellClassName } = styles ?? {};

  const colSpan =
    leftColumnCount + rightColumnCount + (isDraggable ? 1 : 0) + (renderSpacer ? 1 : 0);

  const hasRows = rows.length > 0;
  const hasEmptyGroups = emptyGroups.length > 0;

  return (
    <>
      {emptyGroups.map((emptyGroup) => (
        <TableGroupRow
          key={emptyGroup.id}
          colSpan={colSpan}
          rowClassName={groupRowClassName}
          cellClassName={groupCellClassName}>
          {getEmptyGroupNode?.(emptyGroup)}
        </TableGroupRow>
      ))}

      {hasRows &&
        rows.map((row) => {
          if (row.getIsGrouped()) {
            return (
              <TableGroupRow
                key={row.id}
                colSpan={colSpan}
                rowClassName={groupRowClassName}
                cellClassName={groupCellClassName}>
                {getGroupedNode?.(row)}
              </TableGroupRow>
            );
          }

          const rowKey = isDraggable ? resolveDraggableRowId(row, draggableId) : row.id;

          return (
            <TableDataRow
              key={rowKey}
              row={row}
              isDraggable={isDraggable}
              draggableId={draggableId}
              tableId={tableId}
              renderSpacer={renderSpacer}
              rowClassName={rowClassName}
              cellClassName={cellClassName}
            />
          );
        })}

      {!hasRows && isLoading && (
        <TableSkeletonRows
          rowCount={skeletonRowCount}
          leftColumnCount={leftColumnCount}
          rightColumnCount={rightColumnCount}
          isDraggable={isDraggable}
          renderSpacer={renderSpacer}
        />
      )}

      {!hasRows && !isLoading && !hasEmptyGroups && (
        <TableEmptyState colSpan={colSpan} message={emptyMessage} state={emptyState} />
      )}
    </>
  );
};
